import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { api } from '../../helpers/apiUtils';
import { formatNumber } from '../../helpers/numberUtils';
import SamHtmlPreviewDialog, { SamHtmlPreviewData } from './SamHtmlPreviewDialog';

/**
 * Scoring & Auswahl tab — GLOBAL score-calculation table for the unified
 * External-SAM picker.
 *
 * Reworked 2026-08-04 from the per-client Bayesian view: the live 19:30
 * unified picker ranks GLOBAL-only (buildUnifiedSelectionSql), so this tab
 * shows the whole scored pool with the exact per-component point breakdown,
 * the unified rotation status per SAM, the copy-level euro-ROI30 (info-only —
 * since 2026-08-09 the score's 50%-ROI component is the PRODUCT-level
 * SAM-channel euro-ROI30, unsub rate is display-only) and an HTML preview
 * per SAM. Sortable columns. Read-only.
 *
 * Backend: GET /api/adserver/external-sams/scoring
 */

interface ScoringFormula {
  revenue_per_user_6m_weight: number;
  revenue_per_user_all_time_weight: number;
  product_roi_30_weight: number;
  roi_null_fallback: number;
  high_score_cooldown_threshold: number;
}

interface UnifiedRotation {
  main_sam_cooldown_days: number;
  high_score_sam_cooldown_days: number;
  main_product_cooldown_days: number;
  dup_cooldown_days: number;
  precutover_sam_cooldown_days: number;
  precutover_product_cooldown_days: number;
  cutover_date: string;
}

interface ScoringSam {
  sam_id: number;
  subject: string | null;
  from_name: string | null;
  product_id: number;
  customer: string | null;
  product_name: string | null;
  material_active: number;
  product_active: number;
  has_template: number;
  ft_branding_excluded: number;
  score: number;
  revenue_per_user_6m: string | number | null;
  revenue_per_user_all_time: string | number | null;
  product_roi_30: string | number | null;
  unsubscription_rate_6m: string | number | null;
  lead_rate_6m: string | number | null;
  sum_recipients: number | null;
  sum_leads: number | null;
  sum_revenue: string | number | null;
  sum_sois: number | null;
  first_send: string | null;
  last_send: string | null;
  product_last_send: string | null;
  product_last_send_sam_id: number | null;
  latest_mailing_id: number | null;
  blocked_ft_l1: number;
  blocked_sam_rotation: number;
  blocked_product_rotation: number;
  points_rev_6m: number;
  points_rev_all_time: number;
  points_roi: number;
  roi30_sam_pct: number | null;
  roi30_sam_n: number;
  roi30_sam_costs: number;
}

interface ScoringResponse {
  scoring_formula: ScoringFormula;
  unified_rotation: UnifiedRotation;
  pool_maxima: {
    revenue_per_user_6m: number;
    revenue_per_user_all_time: number;
    product_roi_30: number;
    unsubscription_rate_6m: number;
  };
  allowed_customers: string[];
  next_send_date: string;
  roi30_window: { start: string; end: string };
  sams: ScoringSam[];
  as_of: string;
}

// ── Formatters ──────────────────────────────────────────────────────────

const toNumber = (v: string | number | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : null;
};

const fmtEtkp = (rpu: string | number | null | undefined): string => {
  const n = toNumber(rpu);
  if (n === null) return '–';
  return `${formatNumber(n * 1000, 2)} €`;
};

const fmtPct = (rate: string | number | null | undefined, decimals = 3): string => {
  const n = toNumber(rate);
  if (n === null) return '–';
  return `${formatNumber(n * 100, decimals)} %`;
};

/** Value that already IS a percentage (roi30_*_pct) — no ×100. */
const fmtPctPlain = (pct: number | null | undefined, decimals = 1): string => {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return '–';
  return `${formatNumber(pct, decimals)} %`;
};

const daysSince = (datetime: string | null | undefined): number | null => {
  if (!datetime) return null;
  const t = Date.parse(datetime);
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 86_400_000);
};

const fmtRelativeDate = (datetime: string | null | undefined): string => {
  const d = daysSince(datetime);
  if (d === null) return '–';
  if (d === 0) return 'heute';
  if (d === 1) return 'gestern';
  if (d < 0) return d === -1 ? 'morgen (geplant)' : `in ${-d}d (geplant)`;
  return `vor ${d}d`;
};

// ── Pickability (mirrors buildUnifiedSelectionSql's filters) ─────────────

interface SamStatus {
  pickable: boolean;
  /** Short chip label of the FIRST blocking reason (filter order). */
  label: string;
  /** All blocking reasons, for the tooltip. */
  reasons: string[];
}

function deriveStatus(sam: ScoringSam, allowedCustomers: string[]): SamStatus {
  const reasons: string[] = [];
  if (!sam.material_active || !sam.product_active) reasons.push('inaktiv (Material/Produkt)');
  if (!sam.has_template) reasons.push('kein Classic-Template (Render-Guard)');
  if (sam.ft_branding_excluded) reasons.push('FT-Branding ausgeschlossen');
  // Empty intersection is NOT "no filter": the scheduler aborts the whole
  // unified run on it (critical alert, every client skipped) — mirror that,
  // don't show green chips on a day where nothing would send.
  if (allowedCustomers.length === 0) {
    reasons.push('Customer-Intersection LEER — Unified-Pick fällt aus');
  } else if (sam.customer && !allowedCustomers.includes(sam.customer)) {
    reasons.push(`Customer ${sam.customer} nicht erlaubt`);
  }
  if (sam.blocked_ft_l1) reasons.push('FT-L1 Cooldown (7d SAM / 3d Produkt)');
  if (sam.blocked_sam_rotation) reasons.push('SAM-Rotation');
  if (sam.blocked_product_rotation) reasons.push('Produkt-Rotation');
  if (reasons.length === 0) return { pickable: true, label: '✓ pickbar', reasons: [] };
  const first = reasons[0];
  const label = first.startsWith('inaktiv')
    ? 'inaktiv'
    : first.startsWith('kein Classic')
      ? 'kein Template'
      : first.startsWith('FT-Branding')
        ? 'FT-Branding'
        : first.startsWith('Customer-Intersection')
          ? 'Intersection leer'
          : first.startsWith('Customer')
            ? 'Customer'
            : first.startsWith('FT-L1')
            ? 'FT-L1'
            : first === 'SAM-Rotation'
              ? 'SAM-Rotation'
              : 'Produkt-Rotation';
  return { pickable: false, label, reasons };
}

// ── Sortable columns ────────────────────────────────────────────────────

type SortOrder = 'asc' | 'desc';

interface Row extends ScoringSam {
  status: SamStatus;
  /** 0 = pickable, 1 = blocked — sortable status column. */
  status_rank: number;
}

interface ColumnDef {
  label: string;
  tip: string;
  /** Sortable iff set. Must be a key of Row whose values compare sensibly. */
  key?: keyof Row;
  align?: 'right' | 'left';
  /** Marks the „nicht im Score"-column group (distinct header styling). */
  notInScore?: boolean;
}

const COLUMNS: ColumnDef[] = [
  { label: '#', tip: 'Laufende Nummer in der aktuellen Sortierung' },
  { label: 'SAM', tip: 'SAM-ID, Betreff, Absender, Produkt, Customer (I/GEV)', key: 'sam_id' },
  {
    label: 'Score',
    tip: 'Finaler Score (0–100) = Summe der drei Punkt-Komponenten (Rev/User 6m + Rev/User gesamt + ROI30 Produkt SAM-Kanal), gerundet. Der Unified-Picker sortiert hierauf.',
    key: 'score',
    align: 'right',
  },
  {
    label: 'Rev/User 6m',
    tip: 'UNSER Umsatz (CPL an VNR) pro Empfänger, letzte 6 Monate, als ETKP-Äquivalent (×1000 €). Darunter: Punkte von max. 33,3 (normalisiert auf den Pool-Max).',
    key: 'revenue_per_user_6m',
    align: 'right',
  },
  {
    label: 'Rev/User ges.',
    tip: 'UNSER Umsatz pro Empfänger, all-time (×1000 €). Darunter: Punkte von max. 16,7.',
    key: 'revenue_per_user_all_time',
    align: 'right',
  },
  {
    label: 'ROI30 Produkt (SAM-Kanal)',
    tip: 'Die 50%-Score-Komponente (seit 2026-08-09): Euro-ROI30 des PRODUKTS über ausschließlich SAM-attribuierte Leads (Coreg/Widgets zählen NICHT — deren Beimischung verzerrte den Wert, z.B. 90,6% SAM-Kanal vs 34,6% andere Kanäle beim selben Produkt). Reife-Fenster 6M..33d. Ohne SAM-Kanal-Historie → 25 Punkte Fallback.',
    key: 'product_roi_30',
    align: 'right',
  },
  {
    label: 'Unsub 6m',
    tip: 'Abmelderate der letzten 6 Monate — reine Info, seit 2026-08-09 NICHT mehr gewichtet (der frühere 15%-Anteil hatte gemessen 0,05 Punkte Spread über den ganzen Pool).',
    key: 'unsubscription_rate_6m',
    align: 'right',
  },
  {
    label: 'ROI30 € SAM',
    tip: 'NICHT im Score. Gemessenes VNR-ROI30 der Leads DIESER Copy: Σ abrechnungswert_30 / Σ costs × 100. Fenster: 6 Monate bis 33 Tage zurück (jüngere Leads sind rechts-zensiert). n = Leads im Fenster. Vorsicht bei kleinem n — Copy-Level ist deutlich rauschanfälliger als die Produkt-Komponente links.',
    key: 'roi30_sam_pct',
    align: 'right',
    notInScore: true,
  },
  {
    label: 'Empf. 6m',
    tip: 'Empfänger-Summe der Main-Sends der letzten 6 Monate (dup_send=0, Score-Datengrundlage)',
    key: 'sum_recipients',
    align: 'right',
  },
  {
    label: 'Leads 6m',
    tip: 'Attribuierte Leads der letzten 6 Monate (Main-Sends)',
    key: 'sum_leads',
    align: 'right',
  },
  {
    label: 'Lead-Rate',
    tip: 'Leads / Empfänger (6 Monate)',
    key: 'lead_rate_6m',
    align: 'right',
  },
  {
    label: 'Letzter Versand',
    tip: 'Letzter Main-Send dieses SAMs — LIVE aus den Mailing-Rows, inkl. heute bereits gelaufener und geplanter Sends (der nächtliche Score-Cache hinkt hier bis zum Folgetag hinterher). Darunter: letzter Main-Send des PRODUKTS über alle Schwester-Copys — die Produkt-Rotation (12d) blockiert alle Copys eines Produkts, egal welche zuletzt lief. Orange, wenn der Schwester-Send jünger als 12 Tage ist.',
    key: 'last_send',
  },
  {
    label: 'Status',
    tip: 'Pickbarkeit für den NÄCHSTEN Versandtag, exakt nach den Filtern des Unified-Pickers: aktiv + Template + Customer-Whitelist + FT-L1 + globale SAM-/Produkt-Rotation. Tooltip am Chip listet alle Gründe.',
    key: 'status_rank',
  },
  { label: 'Vorschau', tip: 'SAM-HTML ansehen (letzter Versand, sonst Material-Render)' },
];

/**
 * Fully-numeric values (incl. DECIMAL-as-string from mysql2) compare
 * numerically. ISO date strings deliberately fall through to localeCompare
 * (lexicographic = chronological) — parseFloat would truncate them to the year.
 */
const isNumericLike = (v: unknown): boolean =>
  typeof v === 'number' || (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v.trim()));

/** Comparator; nulls/undefined/'' always sort last (re-partitioned in sortRows). */
function compareValues(a: unknown, b: unknown): number {
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (isNumericLike(a) && isNumericLike(b)) return Number(a) - Number(b);
  return String(a).localeCompare(String(b), 'de');
}

function sortRows(rows: Row[], orderBy: keyof Row, order: SortOrder): Row[] {
  const sorted = [...rows].sort((a, b) => {
    const cmp = compareValues(a[orderBy], b[orderBy]);
    if (cmp !== 0) return order === 'asc' ? cmp : -cmp;
    return b.score - a.score; // stable secondary: score desc
  });
  // nulls-last must survive direction flips: re-partition
  const nonNull = sorted.filter((r) => {
    const v = r[orderBy];
    return v !== null && v !== undefined && (v as unknown as string) !== '';
  });
  const nulls = sorted.filter((r) => !nonNull.includes(r));
  return [...nonNull, ...nulls];
}

// ── Component ───────────────────────────────────────────────────────────

const ExternalScoringTab: React.FC = () => {
  const [data, setData] = useState<ScoringResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orderBy, setOrderBy] = useState<keyof Row>('score');
  const [order, setOrder] = useState<SortOrder>('desc');
  const [preview, setPreview] = useState<SamHtmlPreviewData | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoadingId, setPreviewLoadingId] = useState<number | null>(null);

  const loadScoring = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: response } = await api.get('/api/adserver/external-sams/scoring');
      setData(response?.data ?? null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadScoring();
  }, [loadScoring]);

  const rows: Row[] = useMemo(() => {
    if (!data) return [];
    return data.sams.map((sam) => {
      const status = deriveStatus(sam, data.allowed_customers);
      return { ...sam, status, status_rank: status.pickable ? 0 : 1 };
    });
  }, [data]);

  const sortedRows = useMemo(() => sortRows(rows, orderBy, order), [rows, orderBy, order]);

  // What the next 19:30 run would pick: highest score among pickable rows —
  // independent of the current table sort. Tie-break mirrors the picker's
  // ORDER BY score DESC, sam_id DESC (buildUnifiedSelectionSql).
  const predictedSamId = useMemo(() => {
    const eligible = rows.filter((r) => r.status.pickable);
    if (eligible.length === 0) return null;
    return eligible.reduce(
      (best, r) =>
        r.score > best.score || (r.score === best.score && r.sam_id > best.sam_id) ? r : best,
      eligible[0],
    ).sam_id;
  }, [rows]);
  const eligibleCount = useMemo(() => rows.filter((r) => r.status.pickable).length, [rows]);

  const handleSort = (key: keyof Row) => {
    if (orderBy === key) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(key);
      // Text-ish columns start ascending, metrics descending.
      setOrder(key === 'sam_id' || key === 'status_rank' ? 'asc' : 'desc');
    }
  };

  const handlePreview = async (row: Row) => {
    setError(null);
    setPreviewLoadingId(row.sam_id);
    try {
      if (row.latest_mailing_id) {
        // Real sent/scheduled HTML (cache → live Mailchimp), same as the other tabs.
        const { data: resp } = await api.get(
          `/api/adserver/external-sams/preview/${row.latest_mailing_id}`,
        );
        if (resp?.success) {
          setPreview(resp.data as SamHtmlPreviewData);
          setPreviewOpen(true);
        } else {
          setError(resp?.error ?? 'Vorschau fehlgeschlagen');
        }
      } else {
        // Never sent — FT AdServer material render.
        const { data: resp } = await api.get(
          `/api/adserver/external-sams/material-preview/${row.sam_id}`,
        );
        if (resp?.success) {
          setPreview({
            id: row.sam_id,
            client_code: null,
            send_date: '',
            status: 'pool',
            mc_campaign_id: null,
            mc_campaign_title: null,
            material_type: 'sam',
            asset_isin: null,
            asset_name: null,
            sam_subject: row.subject,
            html: resp.data?.html ?? null,
            html_source: resp.data?.html ? 'ft_render' : 'none',
            source_error: resp.data?.source_error ?? null,
          });
          setPreviewOpen(true);
        } else {
          setError(resp?.error ?? 'Material-Vorschau fehlgeschlagen');
        }
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPreviewLoadingId(null);
    }
  };

  return (
    <Box>
      {/* Header summary */}
      <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
        {data && (
          <Typography variant="body2" color="text.secondary">
            Pool: <strong>{rows.length}</strong> gescorte SAMs · pickbar für{' '}
            {data.next_send_date}: <strong>{eligibleCount}</strong>
            {predictedSamId !== null && (
              <>
                {' '}
                ·{' '}
                <strong style={{ color: '#1976d2' }}>
                  → wahrscheinlicher Pick: SAM-{predictedSamId} (Score{' '}
                  {rows.find((r) => r.sam_id === predictedSamId)?.score})
                </strong>
              </>
            )}
            {' · '}ROI30-€-Fenster: {data.roi30_window.start} bis {data.roi30_window.end}
          </Typography>
        )}
      </Box>

      {/* Explanatory expandable block */}
      <Accordion sx={{ mb: 2, backgroundColor: '#f8f9fc' }} defaultExpanded={false}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            Wie wird der Score berechnet & wie wählt der Unified-Picker aus? — anklicken für
            Erklärung
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ScoringExplanation
            formula={data?.scoring_formula}
            rotation={data?.unified_rotation}
            roiWindow={data?.roi30_window}
          />
        </AccordionDetails>
      </Accordion>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {data && data.allowed_customers.length === 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Die allowed_customers-Intersection über alle aktiven Clients ist LEER — der
          Unified-Picker plant an so einem Tag NICHTS (kritischer Alert im Scheduler).
          external_sam_clients.allowed_customers prüfen.
        </Alert>
      )}

      {loading || !data ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 760 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {COLUMNS.map((c) => (
                  <TableCell
                    key={c.label}
                    align={c.align}
                    sortDirection={c.key && orderBy === c.key ? order : false}
                    sx={{
                      backgroundColor: c.notInScore ? '#efe6f7 !important' : '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      justifyContent={c.align === 'right' ? 'flex-end' : 'flex-start'}
                    >
                      {c.key ? (
                        <TableSortLabel
                          active={orderBy === c.key}
                          direction={orderBy === c.key ? order : 'desc'}
                          onClick={() => handleSort(c.key as keyof Row)}
                        >
                          {c.label}
                        </TableSortLabel>
                      ) : (
                        c.label
                      )}
                      <Tooltip title={c.tip} arrow placement="top">
                        <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      </Tooltip>
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} align="center">
                    Keine SAMs im Pool.
                  </TableCell>
                </TableRow>
              )}
              {sortedRows.map((row, idx) => (
                <TableRow
                  key={row.sam_id}
                  hover
                  sx={{
                    opacity: row.status.pickable ? 1 : 0.55,
                    borderLeft: row.sam_id === predictedSamId ? '4px solid #1976d2' : undefined,
                    backgroundColor:
                      row.sam_id === predictedSamId ? 'rgba(25, 118, 210, 0.06)' : undefined,
                  }}
                >
                  <TableCell>
                    <Typography variant="body2">{idx + 1}</Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 240, maxWidth: 340 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" component="span">
                        #{row.sam_id}
                      </Typography>
                      <Chip
                        size="small"
                        label={row.customer || '?'}
                        sx={{ height: 18, fontSize: '0.65rem' }}
                        color={row.customer === 'I' ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" component="div">
                      {(row.subject || '').slice(0, 70)}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" component="div">
                      {row.from_name || ''}
                      {row.product_name ? ` · ${row.product_name.slice(0, 40)}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 'bold',
                        color:
                          row.score >= (data?.scoring_formula.high_score_cooldown_threshold ?? 60)
                            ? '#2e7d32'
                            : 'text.primary',
                      }}
                    >
                      {row.score}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{fmtEtkp(row.revenue_per_user_6m)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatNumber(row.points_rev_6m, 1)}/33,3 P
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {fmtEtkp(row.revenue_per_user_all_time)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatNumber(row.points_rev_all_time, 1)}/16,7 P
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{fmtPct(row.product_roi_30, 2)}</Typography>
                    <Typography
                      variant="caption"
                      color={row.product_roi_30 === null ? 'warning.main' : 'text.secondary'}
                    >
                      {row.product_roi_30 === null
                        ? `Fallback ${formatNumber(data?.scoring_formula.roi_null_fallback ?? 25, 1)}/50 P`
                        : `${formatNumber(row.points_roi, 1)}/50 P`}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {fmtPct(row.unsubscription_rate_6m, 3)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      nicht gewichtet
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ backgroundColor: 'rgba(156, 39, 176, 0.04)' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 'bold',
                        color:
                          row.roi30_sam_pct === null
                            ? 'text.disabled'
                            : row.roi30_sam_pct >= 30
                              ? '#2e7d32'
                              : row.roi30_sam_pct < 15
                                ? '#c62828'
                                : 'text.primary',
                      }}
                    >
                      {fmtPctPlain(row.roi30_sam_pct)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      n={row.roi30_sam_n}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {row.sum_recipients === null ? '–' : formatNumber(row.sum_recipients, 0)}
                  </TableCell>
                  <TableCell align="right">
                    {row.sum_leads === null ? '–' : formatNumber(row.sum_leads, 0)}
                  </TableCell>
                  <TableCell align="right">{fmtPct(row.lead_rate_6m, 2)}</TableCell>
                  <TableCell>
                    <Typography variant="caption" component="div">
                      {fmtRelativeDate(row.last_send)}
                    </Typography>
                    {row.product_last_send !== null &&
                      row.product_last_send_sam_id !== null &&
                      row.product_last_send_sam_id !== row.sam_id && (
                        <Typography
                          variant="caption"
                          component="div"
                          sx={{
                            color:
                              (daysSince(row.product_last_send) ?? 99) < 12
                                ? 'warning.main'
                                : 'text.secondary',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Produkt {fmtRelativeDate(row.product_last_send)} (#
                          {row.product_last_send_sam_id})
                        </Typography>
                      )}
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        row.status.pickable
                          ? `Erfüllt alle Picker-Filter für ${data?.next_send_date}`
                          : row.status.reasons.join(' · ')
                      }
                      arrow
                    >
                      <Chip
                        size="small"
                        label={row.status.label}
                        color={row.status.pickable ? 'success' : 'default'}
                        variant={row.status.pickable ? 'filled' : 'outlined'}
                        sx={{ height: 22, fontSize: '0.7rem' }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
                      onClick={() => handlePreview(row)}
                      disabled={previewLoadingId !== null}
                    >
                      {previewLoadingId === row.sam_id ? '…' : 'Ansehen'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <SamHtmlPreviewDialog
        open={previewOpen}
        preview={preview}
        onClose={() => setPreviewOpen(false)}
      />
    </Box>
  );
};

// ── Explanation accordion content ──────────────────────────────────────

const ScoringExplanation: React.FC<{
  formula: ScoringFormula | undefined;
  rotation: UnifiedRotation | undefined;
  roiWindow: { start: string; end: string } | undefined;
}> = ({ formula, rotation, roiWindow }) => {
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Schritt 1 — Globaler Score (0–100)</strong>, nightly berechnet aus den letzten 6
        Monaten (Main-Sends aller Clients zusammen):
      </Typography>
      <Box
        component="pre"
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          backgroundColor: '#fff',
          p: 1,
          mb: 2,
          border: '1px solid #ddd',
        }}
      >
        {`score = ${formula?.revenue_per_user_6m_weight ?? 33.3} P × (rev/user 6m ÷ Pool-Max)          ← UNSER Umsatz pro Empfänger, jüngstes Signal
      + ${formula?.revenue_per_user_all_time_weight ?? 16.7} P × (rev/user all-time ÷ Pool-Max)     ← Langzeit-Stabilität
      + ${formula?.product_roi_30_weight ?? 50} P × (Produkt-€-ROI30 SAM-Kanal ÷ Pool-Max)  ← VNR-Seite: Σ abrechnungswert_30 ÷ Σ costs, Produkt-Ebene, NUR SAM-Leads (${formula?.roi_null_fallback ?? 25} P wenn NULL)

Die Punkte-Spalten der Tabelle zeigen genau diese drei Summanden.`}
      </Box>

      <Typography variant="body2" sx={{ mb: 2 }}>
        <strong>Seit 2026-08-09</strong>: Die ROI-Komponente wiegt 50% und misst Euro-ROI30 des
        Produkts über <em>ausschließlich SAM-attribuierte Leads</em> — vorher zählte sie
        Order-Anzahlen über alle Kanäle, was den Wert durch Coreg-/Widget-Beimischung verzerrte
        (gemessen: 90,6% SAM-Kanal vs. 34,6% andere Kanäle beim selben Produkt). Reife-Fenster: 6
        Monate bis 33 Tage zurück. Die <strong>Abmelderate ist keine Score-Komponente mehr</strong>{' '}
        — die frühere 15-Punkte-Komponente hatte über den ganzen Pool gemessen nur 0,05 Punkte
        Spread (alle Raten liegen zwischen 0,06% und 0,33%); sie bleibt als Info-Spalte sichtbar.
      </Typography>

      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Schritt 2 — Unified-Auswahl</strong> (seit 2026-07-17): EIN globaler Pick pro Tag
        für alle Clients (Main + Dup für den anderen Customer), sortiert nach{' '}
        <code>Score DESC</code>. Es gibt keine per-Client-Auswahl mehr — die frühere
        Bayesian-Shrinkage läuft nur noch als Fallback-Datenbasis weiter.
      </Typography>
      <Box component="ul" sx={{ fontSize: '0.85rem', mt: 0, mb: 2, '& li': { mb: 0.3 } }}>
        <li>
          <strong>Globale SAM-Rotation</strong>: {rotation?.main_sam_cooldown_days ?? 20}d nach
          einem Main-Send — bzw. nur <strong>{rotation?.high_score_sam_cooldown_days ?? 7}d</strong>{' '}
          für Stars mit Score ≥ {formula?.high_score_cooldown_threshold ?? 60};{' '}
          {rotation?.dup_cooldown_days ?? 4}d nach einem Dup-Send.
        </li>
        <li>
          <strong>Globale Produkt-Rotation</strong>: {rotation?.main_product_cooldown_days ?? 12}d
          nach Main, {rotation?.dup_cooldown_days ?? 4}d nach Dup — egal welches SAM des Produkts.
          Deshalb kann ein SAM blockiert sein, obwohl es selbst lange nicht lief: die Zeile
          „Produkt …“ in der Versand-Spalte zeigt den jüngsten Schwester-Send (orange = innerhalb
          der 12 Tage).
        </li>
        <li>
          <strong>FT-L1</strong>: 7d same-SAM / 3d same-Produkt gegen die FT-Pipeline
          (mc_automated_sams).
        </li>
        <li>
          <strong>Basis-Filter</strong>: Material + Produkt aktiv, Classic-Template vorhanden
          (Render-Guard), kein FT-Branding, Customer in der Whitelist aller Clients.
        </li>
        <li>
          SAMs ohne Score-Zeile (nie versendet) fehlen in dieser Tabelle — der Picker lässt sie
          über <code>COALESCE(score, 0)</code> ans Pool-Ende zu.
        </li>
      </Box>

      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>„Nicht im Score“ — die ROI30-€-SAM-Spalte</strong>: dieselbe Euro-Metrik (
        <code>Σ abrechnungswert_30 ÷ Σ costs × 100</code>), aber auf <em>Copy-Ebene</em> — nur die
        Leads dieses einen SAMs. Die Score-Komponente aggregiert stattdessen auf Produkt-Ebene über
        alle SAM-Sends des Produkts, weil Copy-Level bei kleinem n zu rauschanfällig für die
        Auswahl ist (eine einzelne Copy kann 0,2% zeigen, während das Produkt im SAM-Kanal
        &gt;90% läuft). Fenster:{' '}
        {roiWindow ? `${roiWindow.start} bis ${roiWindow.end}` : '6 Monate bis 33 Tage zurück'}{' '}
        (jüngere Leads sind rechts-zensiert, abrechnungswert_30 friert erst nach ~33 Tagen ein).
      </Typography>
    </Box>
  );
};

export default ExternalScoringTab;
